import { useState, useCallback } from 'react'
import toast from 'react-hot-toast'

export interface Toast {
  id: string
  title?: string
  description?: string
  action?: React.ReactNode
  variant?: 'default' | 'destructive'
}

// Check if dark mode is active
const isDarkMode = () => {
  return document.documentElement.classList.contains('dark')
}

// Get theme-aware toast styles
const getToastStyles = (variant: 'default' | 'destructive' = 'default') => {
  const isDark = isDarkMode()
  
  const baseStyle = {
    background: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    color: isDark ? '#f1f5f9' : '#1e293b',
    border: isDark ? '1px solid rgba(71, 85, 105, 0.3)' : '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    padding: '16px 20px',
    backdropFilter: 'blur(12px)',
    maxWidth: '400px',
  }

  if (variant === 'destructive') {
    return {
      ...baseStyle,
      color: isDark ? '#fca5a5' : '#dc2626',
      border: isDark ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
      boxShadow: isDark 
        ? '0 10px 40px rgba(239, 68, 68, 0.2), 0 4px 16px rgba(239, 68, 68, 0.15)' 
        : '0 10px 40px rgba(239, 68, 68, 0.15), 0 4px 16px rgba(239, 68, 68, 0.1)',
    }
  }

  return {
    ...baseStyle,
    color: isDark ? '#86efac' : '#059669',
    border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.2)',
    boxShadow: isDark 
      ? '0 10px 40px rgba(16, 185, 129, 0.2), 0 4px 16px rgba(16, 185, 129, 0.15)' 
      : '0 10px 40px rgba(16, 185, 129, 0.15), 0 4px 16px rgba(16, 185, 129, 0.1)',
  }
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback(({ title, description, variant = 'default' }: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const message = description || title || (variant === 'destructive' ? 'An error occurred' : 'Success')
    
    const toastStyle = getToastStyles(variant)
    
    if (variant === 'destructive') {
      toast.error(message, {
        style: toastStyle,
        iconTheme: {
          primary: isDarkMode() ? '#ef4444' : '#dc2626',
          secondary: isDarkMode() ? '#1e293b' : '#ffffff',
        },
      })
    } else {
      toast.success(message, {
        style: toastStyle,
        iconTheme: {
          primary: isDarkMode() ? '#10b981' : '#059669',
          secondary: isDarkMode() ? '#1e293b' : '#ffffff',
        },
      })
    }

    const newToast: Toast = { id, title, description, variant }
    setToasts(prev => [...prev, newToast])

    // Auto remove after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 5000)

    return id
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
    toast.dismiss()
  }, [])

  return {
    toast: showToast,
    dismiss: dismissToast,
    toasts,
  }
}